import * as state from '../state';
import * as localState from '../state';
import { Cuboid, CuboidProps, CuboidVariants } from '@abyss/3d-viewer';
import { useCallback } from 'react';
import { AnalysisCuboid, FindViewpointsToolState } from '@/types';
import { CUSTOM_BLOCK_CUBOID_STYLES } from '../constants';
import { useAtomValue, useSetAtom } from 'jotai';
import * as findViewpointsState from '../../findViewpointsTool/state';
import { PoiStatus } from '@/__generated__/graphql';
import * as cuboidState from '@/components/Analysis/modules/cuboids/state';

export const CustomBlockCuboids = () => {
  const customBlockCuboids = useAtomValue(state.customBlockCuboids);
  const selectedCustomBlock = useAtomValue(localState.selectedCustomBlock);
  const setHoveredCustomBlockId = useSetAtom(localState.hoveredCustomBlockId);
  const isAddingWorkscope = useAtomValue(cuboidState.isAddingWorkscope);
  const currentCuboid = useAtomValue(cuboidState.currentCuboid);
  const isIsolatedWorkscope = useAtomValue(cuboidState.isIsolatedWorkscope);
  const selectedSavedBlockId = useAtomValue(state.selectedSavedBlockId);

  const findViewpointsToolState = useAtomValue(findViewpointsState.findViewpointsToolState);
  const isFindViewpointsToolActive = findViewpointsToolState !== FindViewpointsToolState.INACTIVE;

  const getCuboidStyles = useCallback(
    (status?: PoiStatus) => {
      const variant = isFindViewpointsToolActive
        ? CuboidVariants.LinesOnly
        : CuboidVariants.LinesAndTransparentMesh;

      // Rules: https://abyss-solutions.atlassian.net/browse/FABW-4767
      const styledVariantKey = (() => {
        switch (status) {
          case PoiStatus.Completed:
          case PoiStatus.Remediated:
            return 'green';

          case PoiStatus.Inprogress:
            return 'blue';

          case PoiStatus.Inreview:
          case PoiStatus.ChaseItemAdded:
            return 'yellow';

          case undefined:
          case null:
          default:
            return 'white';
        }
      })();

      const style = { ...CUSTOM_BLOCK_CUBOID_STYLES[styledVariantKey].style, variant };
      const hoveredStyle = {
        ...CUSTOM_BLOCK_CUBOID_STYLES[styledVariantKey].hoveredStyle,
        variant,
      };
      return { style, hoveredStyle };
    },
    [isFindViewpointsToolActive]
  );

  const makeCuboidProps = useCallback(
    (cuboid: AnalysisCuboid) => {
      const { id, name, position, rotation, scale, status } = cuboid;

      const cuboidProps: CuboidProps = {
        id,
        name: name ?? id,
        position,
        rotation,
        scale,
        ...getCuboidStyles(status),
      };
      return cuboidProps;
    },
    [getCuboidStyles]
  );

  const onHover = useCallback(
    ({ isHovering, id }: { isHovering: boolean; id: string }) => {
      setHoveredCustomBlockId(isHovering ? id : undefined);
    },
    [setHoveredCustomBlockId]
  );

  if (isAddingWorkscope || !!currentCuboid || isIsolatedWorkscope) return <></>;

  // Hide all of the blocks when a custom block is selected, i.e in isolation mode
  if (selectedCustomBlock || selectedSavedBlockId) return <></>;

  return (
    <>
      {customBlockCuboids.map((block) => {
        const props = makeCuboidProps(block);
        return (
          <Cuboid
            {...props}
            onHover={(isHovering: boolean) => {
              onHover({ isHovering, id: props.id });
            }}
            key={props.id}
            id={props.id}
          />
        );
      })}
    </>
  );
};
